/** Match floating coupons (`Coupon.js` / `Coupon.css`). */
export const TICKET_CORNER_R = 6;

/** Main canvas + list: smaller notches, vertically centered. */
export const TICKET_VARIANT_CLASSIC = "classic";
/** Legacy stub: larger notches, one-third up from bottom (unused by modal). */
export const TICKET_VARIANT_STUB = "stub";
/**
 * Modal coupon view: larger notches; perforation & cutouts at a fixed distance
 * from the top (clamped so they stay inside the ticket for any height).
 */
export const TICKET_VARIANT_MODAL = "modal";

/** Horizontal perforation / notch center: px from top of ticket (before clamp). */
export const MODAL_TICKET_PERFORATION_FROM_TOP = 196;

const NOTCH_R = {
  [TICKET_VARIANT_CLASSIC]: 9,
  [TICKET_VARIANT_STUB]: 14,
  [TICKET_VARIANT_MODAL]: 14,
};

export function ticketNotchRadius(variant = TICKET_VARIANT_STUB) {
  return NOTCH_R[variant] ?? NOTCH_R[TICKET_VARIANT_MODAL];
}

/** @deprecated use ticketNotchRadius(TICKET_VARIANT_MODAL) */
export const TICKET_NOTCH_R = NOTCH_R[TICKET_VARIANT_MODAL];

/**
 * Vertical center of the side notches.
 * @param {number} H
 * @param {string} [variant]
 * @param {number} [preferredNotchY] Modal only: desired Y before clamp (e.g. from content measure).
 */
export function ticketNotchCenterY(
  H,
  variant = TICKET_VARIANT_MODAL,
  preferredNotchY
) {
  const c = TICKET_CORNER_R;
  const n = ticketNotchRadius(variant);
  if (variant === TICKET_VARIANT_CLASSIC) {
    return H / 2;
  }
  if (variant === TICKET_VARIANT_MODAL) {
    const y =
      preferredNotchY != null
        ? preferredNotchY
        : MODAL_TICKET_PERFORATION_FROM_TOP;
    return Math.min(
      H - c - n - 0.01,
      Math.max(c + n + 0.01, y)
    );
  }
  const cyPreferred = (2 * H) / 3;
  return Math.min(
    H - c - n - 0.01,
    Math.max(c + n + 0.01, cyPreferred)
  );
}

/**
 * Horizontal segment between the inner tips of the left/right notches.
 * @param {number} W
 * @param {number} H
 * @param {string} [variant]
 * @param {number} [preferredNotchY]
 */
export function ticketPerforationLine(
  W,
  H,
  variant = TICKET_VARIANT_MODAL,
  preferredNotchY
) {
  const n = ticketNotchRadius(variant);
  const cy = ticketNotchCenterY(H, variant, preferredNotchY);
  return { x1: n, y1: cy, x2: W - n, y2: cy };
}

/**
 * Closed ticket path: rounded corners + semicircular notches on left/right.
 * @param {number} W
 * @param {number} H
 * @param {string} [variant]
 * @param {number} [preferredNotchY]
 */
export function ticketPath(
  W,
  H,
  variant = TICKET_VARIANT_MODAL,
  preferredNotchY
) {
  const c = TICKET_CORNER_R;
  const n = ticketNotchRadius(variant);
  const cy = ticketNotchCenterY(H, variant, preferredNotchY);
  return `M ${c} 0 L ${W - c} 0 A ${c} ${c} 0 0 1 ${W} ${c} L ${W} ${cy - n} A ${n} ${n} 0 1 0 ${W} ${cy + n} L ${W} ${H - c} A ${c} ${c} 0 0 1 ${W - c} ${H} L ${c} ${H} A ${c} ${c} 0 0 1 0 ${H - c} L 0 ${cy + n} A ${n} ${n} 0 1 0 0 ${cy - n} L 0 ${c} A ${c} ${c} 0 0 1 ${c} 0 Z`;
}
